import { api, describeError, problemErrorCode, stepUpHeaders, type StepUpProof } from './api';

/**
 * Ordens de serviço para o agente de escrita (change platform-mcp-writes, tarefa 1.17).
 *
 * A pessoa cria a OS na página da loja ("Delegar ao agente") ou em lote ("Lojas para agir"); o
 * agente só age sobre o que uma OS aberta autoriza. A loja vem sempre do `tenantId` da página ou da
 * linha marcada, nunca de um nome digitado. Os máximos começam no mínimo (1 dia, 1 efeito) e a
 * validade em 24 h. A criação pede o step-up (TOTP, ou passkey onde ela vale) e leva
 * `Idempotency-Key`. O modo direto exige passkey e ainda não existe (a API o recusa com
 * `platform.agent_work_order.direct_mode_unavailable`).
 */

export const AGENT_WORK_ORDERS_URL = '/platform/agent-work-orders';
export const AGENT_WORK_ORDERS_BATCH_URL = '/platform/agent-work-orders/batch';

/** As permissões das ações equivalentes que já viram OS (trial e concessão). */
export const AGENT_WORK_ORDER_PERMISSIONS = ['platform.trial.extend', 'platform.concessions.write'] as const;

export type AgentWorkOrderKind = 'trial_extension' | 'concession';

export const AGENT_WORK_ORDER_KIND_LABELS: Readonly<Record<string, string>> = {
  trial_extension: 'Propor extensão de trial',
  trial_extension_cancel: 'Propor cancelamento de extensão de trial',
  concession: 'Propor concessão de acesso',
  concession_cancel: 'Propor encerramento de concessão',
  plan_override: 'Propor cortesia de plano',
  plan_override_cancel: 'Propor encerramento de cortesia',
  order_note: 'Nota interna em pedido',
  credit: 'Propor crédito na fatura',
  discount: 'Propor desconto',
  discount_revoke: 'Propor revogação de desconto',
};

export const AGENT_WORK_ORDER_STATUS_LABELS: Readonly<Record<string, string>> = {
  Open: 'Aberta',
  Exhausted: 'Esgotada',
  Expired: 'Vencida',
  Cancelled: 'Cancelada',
  Unknown: 'Fechada',
};

export function kindLabel(kind: string): string {
  return Object.hasOwn(AGENT_WORK_ORDER_KIND_LABELS, kind) ? AGENT_WORK_ORDER_KIND_LABELS[kind] : kind;
}

export function statusLabel(status: string): string {
  return Object.hasOwn(AGENT_WORK_ORDER_STATUS_LABELS, status) ? AGENT_WORK_ORDER_STATUS_LABELS[status] : 'Fechada';
}

export interface AgentWorkOrderDto {
  id: string;
  tenantId: string | null;
  agentClientId: string;
  agentProfileName: string;
  kind: string;
  mode: string;
  maxDays: number | null;
  maxEffects: number;
  effectsUsed: number;
  status: string;
  stepUpMethod: string;
  createdAt: string;
  expiresAt: string;
  closedAt: string | null;
  createdByAccountId: string;
  createdByName: string;
}

export interface AgentWorkOrderKindOption {
  kind: AgentWorkOrderKind;
  requiredPermission: string;
  maxDaysCeiling: number | null;
  batchable: boolean;
}

export interface AgentWorkOrderRecipient {
  id: string;
  /** `OAuthClientId` na API, em camelCase do System.Text.Json. */
  oAuthClientId: string;
  profileName: string;
  kinds: string[];
}

export interface AgentWorkOrderOptions {
  kinds: AgentWorkOrderKindOption[];
  recipients: AgentWorkOrderRecipient[];
  defaultValidForHours: number;
  maxValidForHours: number;
  maxEffects: number;
  maxBatchSize: number;
  directModeAvailable: boolean;
}

/** O que o formulário manda; o alvo (`tenantId` ou `tenantIds`) entra na chamada. */
export interface AgentWorkOrderForm {
  agentClientId: string;
  kind: AgentWorkOrderKind;
  mode: 'Propose';
  maxDays: number;
  maxEffects: number;
  validForHours: number;
}

/** O formulário com o mínimo: 1 dia, 1 efeito, a validade padrão e o primeiro destinatário do tipo. */
export function minimumForm(options: AgentWorkOrderOptions, kind?: AgentWorkOrderKind): AgentWorkOrderForm | null {
  const chosen = options.kinds.find((k) => k.kind === kind) ?? options.kinds[0];
  if (!chosen) return null;
  const recipient = options.recipients.find((r) => r.kinds.includes(chosen.kind));
  return {
    agentClientId: recipient?.id ?? '',
    kind: chosen.kind,
    mode: 'Propose',
    maxDays: 1,
    maxEffects: 1,
    validForHours: options.defaultValidForHours,
  };
}

/** O formulário cabe nos limites que a API anunciou (o servidor confere de novo). */
export function isFormValid(form: AgentWorkOrderForm, options: AgentWorkOrderOptions): boolean {
  const kind = options.kinds.find((k) => k.kind === form.kind);
  if (!kind) return false;
  const recipient = options.recipients.find((r) => r.id === form.agentClientId);
  if (!recipient || !recipient.kinds.includes(form.kind)) return false;
  const ceiling = kind.maxDaysCeiling ?? 0;
  return (
    Number.isInteger(form.maxDays) && form.maxDays >= 1 && form.maxDays <= ceiling &&
    Number.isInteger(form.maxEffects) && form.maxEffects >= 1 && form.maxEffects <= options.maxEffects &&
    Number.isInteger(form.validForHours) && form.validForHours >= 1 && form.validForHours <= options.maxValidForHours
  );
}

export async function getAgentWorkOrderOptions(): Promise<AgentWorkOrderOptions> {
  const { data } = await api.get<AgentWorkOrderOptions>(`${AGENT_WORK_ORDERS_URL}/options`);
  return data;
}

export async function listAgentWorkOrders(tenantId?: string): Promise<AgentWorkOrderDto[]> {
  const { data } = await api.get<{ items: AgentWorkOrderDto[] }>(AGENT_WORK_ORDERS_URL, {
    params: tenantId ? { tenantId } : undefined,
  });
  return data.items;
}

function writeHeaders(proof: StepUpProof, idempotencyKey: string): Record<string, string> {
  return { 'Idempotency-Key': idempotencyKey, ...stepUpHeaders(proof) };
}

export async function createAgentWorkOrder(
  tenantId: string,
  form: AgentWorkOrderForm,
  proof: StepUpProof,
  idempotencyKey: string,
): Promise<AgentWorkOrderDto> {
  const { data } = await api.post<AgentWorkOrderDto>(
    AGENT_WORK_ORDERS_URL,
    { tenantId, ...form },
    { headers: writeHeaders(proof, idempotencyKey) },
  );
  return data;
}

export async function createAgentWorkOrderBatch(
  tenantIds: string[],
  form: AgentWorkOrderForm,
  proof: StepUpProof,
  idempotencyKey: string,
): Promise<AgentWorkOrderDto[]> {
  const { data } = await api.post<{ items: AgentWorkOrderDto[] }>(
    AGENT_WORK_ORDERS_BATCH_URL,
    { tenantIds, ...form },
    { headers: writeHeaders(proof, idempotencyKey) },
  );
  return data.items;
}

export async function cancelAgentWorkOrder(id: string): Promise<AgentWorkOrderDto> {
  const { data } = await api.post<AgentWorkOrderDto>(`${AGENT_WORK_ORDERS_URL}/${encodeURIComponent(id)}/cancel`);
  return data;
}

/** Mensagens dos códigos da API; o resto cai no `describeError`. */
export const AGENT_WORK_ORDER_MESSAGES: Readonly<Record<string, string>> = {
  'platform.agent_work_order.direct_mode_unavailable':
    'O modo direto exige confirmação por passkey e ainda não está disponível. Use o modo proposta.',
  'platform.agent_work_order.permission_denied':
    'Seu papel não tem a permissão da ação equivalente a esta ordem de serviço.',
  'platform.agent_work_order.recipient_not_eligible':
    'Este agente não pode receber esta ordem de serviço neste ambiente.',
  'platform.agent_work_order.tenant_not_found': 'Loja não encontrada.',
  'platform.agent_work_order.tenant_inactive': 'A loja está desativada.',
  'platform.agent_work_order.kind_unavailable': 'Este tipo de ordem de serviço ainda não está disponível.',
  'platform.agent_work_order.kind_not_batchable':
    'Dinheiro e perda de acesso não saem em lote: crie a ordem na página da loja.',
  'platform.agent_work_order.not_open': 'A ordem de serviço já não está aberta.',
};

export function describeWorkOrderError(err: unknown): string {
  const code = problemErrorCode(err);
  if (code && Object.hasOwn(AGENT_WORK_ORDER_MESSAGES, code)) return AGENT_WORK_ORDER_MESSAGES[code];
  return describeError(err);
}
